import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { IItem } from "../../../shared/model/types";
import { MyInput } from "../../../shared/ui/input";
import { MyButton } from "../../../shared/ui/button";
import { Modal } from "../../../shared/ui/modal";
import classes from "./tagSelector.module.scss";

interface IProps {
    title?: string;
    selectedItems: IItem[];
    onChange: (items: IItem[]) => void;
    getData: () => Promise<IItem[]>;
    error?: string;
    setError?: (err: string) => void;
    searchPlaceholder?: string;
    reloadTrigger?: number;
    placeholder?: string;
    emptyText?: string;
    canCreate?: boolean;
    createPlaceholder?: string;
    createButtonText?: string;
    onCreate?: (tagName: string) => Promise<IItem>;
    singleLine?: boolean;
}

export const TagSelector: FC<IProps> = ({
    title = '',
    selectedItems,
    onChange,
    getData,
    error,
    setError,
    searchPlaceholder = 'Поиск по тегам',
    reloadTrigger,
    placeholder = 'Выберите теги',
    emptyText = 'Теги не найдены',
    canCreate = false,
    createPlaceholder = 'Введите название тега',
    createButtonText = 'Создать тег',
    onCreate,
    singleLine = false,
}) => {
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const [items, setItems] = useState<IItem[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false)
    const [createValue, setCreateValue] = useState<string>('')
    const [createError, setCreateError] = useState<string>('')
    const [isCreating, setIsCreating] = useState<boolean>(false)
    const getDataRef = useRef(getData)

    useEffect(() => {
        getDataRef.current = getData
    }, [getData])

    useEffect(() => {
        const loadItems = async () => {
            try{
                setIsLoading(true)
                const itemsRes = await getDataRef.current()
                setItems(itemsRes)
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении списка тегов', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }

        loadItems()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTrigger])

    const normalizedSelectedItems = useMemo(() => {
        return selectedItems.map(selectedItem =>
            items.find(item => item.id === selectedItem.id || item.name === selectedItem.name) || selectedItem
        )
    }, [items, selectedItems])

    const onSelected = (item: IItem) => {
        return (selected: boolean) => {
            const nextItems = selected
                ? [...normalizedSelectedItems.filter(selectedItem => selectedItem.id !== item.id), item]
                : normalizedSelectedItems.filter(
                    selectedItem => selectedItem.id !== item.id && selectedItem.name !== item.name
                )

            onChange(nextItems)
            setError && setError('')
        }
    }

    const onSelectAll = (selectedIds: number[]) => {
        const availableItems = [...normalizedSelectedItems, ...items]
        const nextItems = selectedIds
            .map(id => availableItems.find(item => item.id === id))
            .filter((item): item is IItem => Boolean(item))

        onChange(nextItems)
        setError && setError('')
    }

    const onCreateTag = async () => {
        const tagName = createValue.trim()

        if(!canCreate || !onCreate){
            return
        }

        if(!tagName){
            setCreateError('Введите название тега')
            return
        }

        try{
            setIsCreating(true)
            setCreateError('')
            const createdTag = await onCreate(tagName)
            setItems(current => {
                const nextItems = [...current.filter(item => item.id !== createdTag.id), createdTag]
                return nextItems.sort((first, second) => first.name.localeCompare(second.name))
            })
            onChange([...normalizedSelectedItems.filter(item => item.id !== createdTag.id), createdTag])
            setCreateValue('')
            setIsCreateOpen(false)
            setError && setError('')
            setGlobalMessage({message: 'Тег создан', type: 'ok'})
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setCreateError(e.message)
            }
            else{
                setCreateError('Ошибка при создании тега')
            }
        }
        finally{
            setIsCreating(false)
        }
    }

    const onCreateTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if(e.key === 'Enter'){
            e.preventDefault()
            onCreateTag()
        }
    }

    return (
        <section className={`${classes.container}${singleLine ? ` ${classes.singleLine}` : ''}`}>
            {title && <section className={classes.title}>{title}</section>}
            <section className={classes.selector}>
                <DropDownListSelected
                    isLoading={isLoading}
                    items={items}
                    selectedIdItems={normalizedSelectedItems.map(item => item.id)}
                    onSelected={onSelected}
                    onSelectAll={onSelectAll}
                    selectedCount
                    placeholder={placeholder}
                    search
                    searchPlaceholder={searchPlaceholder}
                    emptyText={emptyText}
                />
            </section>
            {
                canCreate && onCreate &&
                <button
                    type="button"
                    className={classes.createOptionButton}
                    onClick={() => {
                        setCreateError('')
                        setIsCreateOpen(true)
                    }}
                >
                    {createButtonText}
                </button>
            }
            <section className={classes.errorText}>
                {error && <>*{error}</>}
            </section>
            <Modal open={isCreateOpen} setOpen={setIsCreateOpen}>
                <section className={classes.optionModal}>
                    <h3>Создать тег</h3>
                    <MyInput
                        value={createValue}
                        setValue={value => {
                            setCreateValue(value)
                            setCreateError('')
                        }}
                        placeholder={createPlaceholder}
                        error={createError}
                        setError={setCreateError}
                        isLoading={isCreating}
                        onKeyDown={onCreateTagKeyDown}
                    />
                    <section className={classes.optionModalAction}>
                        <MyButton onClick={onCreateTag} isLoading={isCreating} error={createError}>
                            Создать
                        </MyButton>
                    </section>
                </section>
            </Modal>
        </section>
    )
}
